import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {setCredentials} from "../../../src/features/auth/authSlice";
import config from "../../config";

const PhoneModel = ({ isOpen, closeModal }) => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Timer effect
  useEffect(() => {
    let interval;
    if (isOtpModalOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOtpModalOpen, timer]);

  // 📤 Send OTP API call
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      toast("Please enter a valid 10-digit phone number.");
      return;
    }

    try {
      const res = await fetch(`${config.API_BASE_URL}/user/sendOTP`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phoneNumber,
          type: "register",
          appType: "customer",
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setIsOtpModalOpen(true);
        setTimer(30);
        toast("OTP sent successfully.");
      } else {
        toast(data.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error("Send OTP Error:", error);
      toast("Something went wrong while sending OTP.");
    }
  };

  // 🔐 Login/Verify OTP API call
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    const otpRegex = /^\d{4}$/;

    if (!otpRegex.test(otpValue)) {
      toast("Please enter a valid 4-digit OTP.");
      return;
    }

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/customer/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contact: phoneNumber,
            otp: otpValue,
            app: "customer",
          }),
        }
      );

      const data = await res.json();

      if (res.ok && data.status && data.data?.token) {
        const token = data.data.token;

        // 💾 Save token to localStorage
        localStorage.setItem("token", token);
       dispatch(setCredentials({ token, phone: phoneNumber }));

        toast("Login successful!");
        

        setIsOtpModalOpen(false);
        closeModal();
      } else {
        toast(data.message || "Invalid OTP.");
      }
    } catch (error) {
      console.error("OTP Verify Error:", error);
      toast("Something went wrong while verifying OTP.");
    }
  };

  // 🔁 Resend OTP API call
  const handleResendOtp = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/user/sendOTP`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phoneNumber,
          type: "register",
          appType: "customer",
        }),
      });

      const data = await res.json();
      if (res.ok) {
        toast("OTP resent to " + phoneNumber);
        setOtp(["", "", "", ""]);
        setTimer(30);
        inputRefs[0].current.focus();
      } else {
        toast(data.message || "Failed to resend OTP.");
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      toast("Something went wrong while resending OTP.");
    }
  };

  // OTP Input handler
  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 3) {
        inputRefs[index + 1].current.focus();
      }
      if (!value && index > 0) {
        inputRefs[index - 1].current.focus();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Phone Number Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-4 sm:p-6">
          <div className="flex justify-center mb-4">
            <img src="/fab.png" alt="FAB Logo" className="h-16 sm:h-20" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-[#000048] mb-4 text-center">
            FindLocalPetService
          </h2>
          <p className="text-[#000048] text-sm sm:text-base mb-4 text-center">
            Login or Sign up
          </p>
          <form onSubmit={handlePhoneSubmit}>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setPhoneNumber(value);
              }}
              placeholder="Enter 10-digit phone number"
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:border-orange-500 text-sm sm:text-base"
              maxLength={10}
              pattern="\d{10}"
              required
            />
            <p className="text-[#000048] text-xs sm:text-sm mb-4 text-center">
              By Continuing, you agree to our{" "}
              <a href="/terms" className="text-orange-600 hover:underline">
                Terms of Service
              </a>{" "}
              &{" "}
              <a href="/privacy" className="text-orange-600 hover:underline">
                Privacy Policy
              </a>
              .
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 py-2 sm:py-3 px-4 bg-gray-100 hover:bg-gray-200 text-[#000048] rounded-lg font-semibold text-sm sm:text-base transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-2 sm:py-3 px-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-lg font-semibold text-sm sm:text-base transition-all duration-300"
              >
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* OTP Modal */}
      {isOtpModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-4 sm:p-6">
            <div className="flex justify-center mb-4">
              <img src="/fab.png" alt="FAB Logo" className="h-16 sm:h-20" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#000048] mb-4 text-center">
              OTP Verification
            </h2>
            <p className="text-[#000048] text-sm sm:text-base mb-4 text-center">
              We have sent you a 4-digit OTP to {phoneNumber}
            </p>
            <form onSubmit={handleOtpSubmit}>
              <div className="flex justify-center gap-2 sm:gap-3 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    className="w-12 sm:w-14 h-12 sm:h-14 border border-gray-300 rounded-lg text-center text-lg sm:text-xl font-semibold text-[#000048] focus:outline-none focus:border-orange-500"
                    maxLength={1}
                    pattern="\d"
                    ref={inputRefs[index]}
                    required
                  />
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={timer > 0}
                  className={`text-orange-600 text-sm sm:text-base hover:underline ${
                    timer > 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Resend OTP
                </button>
                <p className="text-orange-600 text-sm sm:text-base">
                  {timer > 0 ? `(in ${timer} secs)` : "(00:00)"}
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setIsOtpModalOpen(false)}
                  className="flex-1 py-2 sm:py-3 px-4 bg-gray-100 hover:bg-gray-200 text-[#000048] rounded-lg font-semibold text-sm sm:text-base transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 sm:py-3 px-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-lg font-semibold text-sm sm:text-base transition-all duration-300"
                >
                  Submit OTP
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneModel;
